import { useAtom } from 'jotai';
import { useEffect } from 'react';

import { useCart } from '$hooks/queries';
import { lastUserClickAndCollectStoreAtom } from '$src/business/atoms/last-click-and-collect-store';

/**
 * Seeds `lastUserClickAndCollectStoreAtom` with the first store of every
 * just-click-and-collect method as soon as the cart's shipping methods
 * arrive. Mirrors the prefetch+seed pattern in `usePrefetchPickupPoints` —
 * the goal is to populate the optimistic-display fallback BEFORE the user
 * has ever tapped the "Boutique" segment, so the first tap renders the
 * store name immediately instead of flashing a spinner while
 * `useAutoSelectClickAndCollectStore` round-trips its mutation.
 *
 * No network call — click-and-collect stores are already embedded in
 * `cart.shipping.methods[*].justClickAndCollectStores` (no separate query
 * exists). This hook just consumes that data once and writes it into the
 * atom, keyed by method id.
 *
 * Never overrides an existing atom entry: only seeds methods that aren't
 * already in the map. Once a user manually picks a store via
 * `handleSelectClickAndCollectStore` (or the autoselect mutation runs),
 * those writes win.
 *
 * Mount once at the top of the Delivery / ShippingMethods tree.
 */
export const usePrefetchClickAndCollectStores = () => {
  const { data: cart } = useCart();
  const [storeMap, setStoreMap] = useAtom(lastUserClickAndCollectStoreAtom);

  useEffect(() => {
    const methods = cart?.shipping?.methods;
    if (!methods || methods.length === 0) return;

    setStoreMap((prev) => {
      let next = prev;
      for (const method of methods) {
        if (!method.id) continue;
        if (method.carriers?.[0]?.name !== 'just-click-and-collect') continue;
        if (prev[method.id]) continue;
        const first = method.justClickAndCollectStores?.[0];
        if (!first) continue;
        if (next === prev) next = { ...prev };
        next[method.id] = first;
      }
      return next;
    });
    // `storeMap` intentionally NOT in deps — re-running on every map mutation
    // would loop. The setter receives the latest map via the updater
    // callback, which is the only access we need.
     
  }, [cart?.shipping?.methods, setStoreMap]);
};
